import { Button, IconButton, TableBody, TableHead, TextField, Typography } from '@mui/material';
import {
  CustomBodyCell,
  CustomHeaderCell,
  CustomTable,
  CustomTableContainer,
  CustomTableRow,
} from '@components/Styles/TableStyle';
import { CustomStack, CustomTextField, CustomAutocomplete, SectionWrapper } from '@components/Styles/CommonStyle';
import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useDialog } from '@context/DialogContext';
import AddIcon from '@mui/icons-material/Add';
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import { CustomButton } from '@components/Styles/ButtonStyle';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';
import { theme } from '@components/Styles/theme';
import { formatAmount } from '@utils/formatter';
import api from '@api/axios';

const ProjectCreateTableFinanceV2 = forwardRef(({ title, getProjectInfo, callFinanceView, isAdmin }, ref) => {
  const projectInfo = getProjectInfo();

  const [startYear, setStartYear] = useState(
    projectInfo ? Number(projectInfo.startDate.split('.')[0]) : Number(moment().format('YYYY')),
  );
  const [endYear, setEndYear] = useState(
    projectInfo ? Number(projectInfo.endDate.split('.')[0]) : Number(moment().format('YYYY')),
  );

  const { showDialog } = useDialog();
  const [selectedYear, setSelectedYear] = useState(startYear);
  const [monthlyFinance, setMonthlyFinance] = useState([[startYear, 1, ...Array(13).fill('')]]);
  const [searchCompanyList, setSearchCompanyList] = useState([]);
  const [searchCompany, setSearchCompany] = useState('');

  const financeType = ['sale', 'purchase', 'externalStaff', 'managementFee'];

  // 테이블 헤더 제목 설정
  let nameTitle = '';
  switch (title) {
    case '매출':
      nameTitle = '매출처';
      break;
    case '매입':
      nameTitle = '매입처';
      break;
    case '외부인건비':
      nameTitle = '성명';
      break;
    case '판관비':
      nameTitle = '구분';
      break;
  }

  // 년도 변경 시에 행 추가 및 제거
  const updateMonthlyRowByYear = (prev) => {
    console.log('ProjectCreateTableFinanceV2 :: updateMonthlyRowByYear');
    console.log(prev);

    const existingYears = new Set(prev.map((row) => row[0])); // 현재 존재하는 연도
    const rowsToAdd = []; // 추가할 행

    // 기존 연도 중에 가장 작은 연도
    const minExistingYear = Math.min(...existingYears);
    // 기존 연도 중에 가장 큰 연도
    const maxExistingYear = Math.max(...existingYears);

    // 과거 연도 추가
    if (startYear < minExistingYear) {
      // startYear :: 예를 들어 2025년 -> 2020년 경우 2020년부터 2025년까지 추가
      for (let year = startYear; year < minExistingYear; year++) {
        if (!existingYears.has(year)) {
          rowsToAdd.push([year, 1, ...Array(13).fill('')]);
        }
      }
    }
    // 미래 연도 추가
    if (endYear > maxExistingYear) {
      for (let year = maxExistingYear + 1; year <= endYear; year++) {
        if (!existingYears.has(year)) {
          rowsToAdd.push([year, 1, ...Array(13).fill('')]);
        }
      }
    }

    // startYear :: 예를 들어 2025년 -> 2030년 경우 2025년부터 2030년까지 제거
    const filterd = prev.filter((row) => row[0] >= startYear && row[0] <= endYear);

    return [...rowsToAdd, ...filterd];
  };

  // 회사 검색 (매출, 매입 경우)
  const onSearchCompany = async (name) => {
    let accountTypes = ['법인사업자', '개인사업자'];

    const requests = accountTypes.map((type) =>
      api.get(`/api/account/readAccountList?accountType=${type}${name ? `&name=${name}` : ''}`),
    );

    try {
      const responses = await Promise.all(requests);

      const allCompanies = responses.flatMap((res) => {
        // console.log('onSearchCompany :: res');
        // console.log(res);
        return res.data.result.rows.map((item) => {
          console.log('onSearchCompany :: item');
          console.log(item);
          if (item.account_type === '법인사업자') {
            return item.company_name;
          } else if (item.account_type === '개인사업자') {
            return item.company_name;
          }
        });
      });

      setSearchCompanyList(allCompanies);
    } catch (error) {
      console.error('검색 중 오류 발생:', error);
    }
  };

  // 프리랜서 검색 (외부인건비 경우)
  const onSearchExternalStaff = (name) => {
    const accountType = '프리랜서';

    api
      .get(
        `/api/account/readAccountList?${accountType ? `&accountType=${accountType}` : ''}${name ? `&name=${name}` : ''}`,
      )
      .then((res) => {
        const list = res.data.result.rows;

        const externalStaffCompanyList = list.map((item) => {
          return item.person_name;
        });

        setSearchCompanyList(externalStaffCompanyList);
      });
  };

  // 금액 변경 함수
  const handleMonthlyFinanceChange = (event, index, month) => {
    // 실적현황 호출 타입 설정
    let financeType = '';
    switch (title) {
      case '매출':
        financeType = 'sale';
        break;
      case '매입':
        financeType = 'purchase';
        break;
      case '외부인건비':
        financeType = 'externalStaff';
        break;
      case '판관비':
        financeType = 'managementFee';
    }

    // console.log("financeType");
    // console.log(financeType);

    // 실적현황 호출
    callFinanceView(financeType);

    let value = '';
    // 값 :: 옵션 선택
    if (event?.type === 'click') {
      value = event.target.innerText;
    }
    // 값 :: 입력
    else {
      value = event.target.value;
    }
    const updated = [...monthlyFinance];
    if (month === 0) updated[index][14] = value;
    else updated[index][month + 1] = value;
    setMonthlyFinance(updated);
  };

  // 행 추가 함수
  const handleAddMonthlyFinance = () => {
    const updated = [...monthlyFinance];
    const newSeq = updated.length + 1;
    const newRow = [selectedYear, newSeq, ...Array(13).fill('')];
    newRow.isAdded = true; // 추가된 행에만 isAdded: true
    updated.push(newRow);
    setMonthlyFinance(updated);
  };

  // 행 삭제 함수
  const handleDeleteMonthlyFinance = (index) => {
    // 행이 1개 일때
    if (monthlyFinance.length === 1) {
      showDialog({
        title: '확인',
        message: '최소 1개 이상의 행이 있어야 합니다.',
      });
      return;
    }
    // 행이 1개 아닐때
    else {
      showDialog({
        type: 'confirm',
        title: '확인',
        message: '정말 삭제하시겠습니까?',
        onConfirm: () => {
          const updated = [...monthlyFinance];
          updated.splice(index, 1);
          setMonthlyFinance(updated);
        },
      });
    }
  };

  // 합계 계산 함수
  const calculateMonthlySum = (row) => {
    return (
      Number(row[2]) + // 1월
      Number(row[3]) + // 2월
      Number(row[4]) + // 3월
      Number(row[5]) + // 4월
      Number(row[6]) + // 5월
      Number(row[7]) + // 6월
      Number(row[8]) + // 7월
      Number(row[9]) + // 8월
      Number(row[10]) + // 9월
      Number(row[11]) + // 10월
      Number(row[12]) + // 11월
      Number(row[13]) // 12월
    );
  };

  useImperativeHandle(ref, () => ({
    getMonthlyFinance: () => monthlyFinance,
    setMonthlyFinance: (info) => setMonthlyFinance(info),
    setStartYear: (year) => setStartYear(year),
    setEndYear: (year) => setEndYear(year),
    setSelectedYear: (year) => setSelectedYear(year),
  }));

  // 자동 검색 기능
  useEffect(() => {
    if (title === '매출' || title === '매입') {
      onSearchCompany(searchCompany);
    } else if (title === '외부인건비') {
      onSearchExternalStaff(searchCompany);
    }
  }, [searchCompany]);

  // 년도 변경 시에 행 추가 및 제거 기능
  useEffect(() => {
    // 재정현황 설정
    setMonthlyFinance(updateMonthlyRowByYear(monthlyFinance));

    setTimeout(() => {
      // 실적현황 호출
      financeType.forEach((type) => {
        callFinanceView(type);
      });
    }, 1000);
  }, [startYear, endYear]);

  return (
    isAdmin && (
    <React.Fragment>
      <SectionWrapper>
        <CustomStack mb={2}>
          <Typography sx={theme.typography.h4}>{title}</Typography>
          {/* 년도 선택 컴포넌트 */}
          <CustomStack>
            <IconButton sx={{ p: 0 }}>
              <ArrowLeftIcon
                fontSize="large"
                onClick={() => {
                  const projectInfo = getProjectInfo();
                  const startDate = projectInfo.startDate;
                  const startYear = moment(startDate).year();
                  const lastYear = selectedYear - 1;
                  if (lastYear >= startYear) {
                    setSelectedYear(lastYear);
                    setStartYear(lastYear);
                  }
                }}
              />
            </IconButton>

            <Button variant="text" sx={{ p: 0, minWidth: '48px', fontSize: '16px', color: '#000' }}>
              {selectedYear}
            </Button>

            <IconButton sx={{ p: 0 }}>
              <ArrowRightIcon
                fontSize="large"
                onClick={() => {
                  const projectInfo = getProjectInfo();
                  const endDate = projectInfo.endDate;
                  const endYear = moment(endDate).year();
                  const nextYear = selectedYear + 1;
                  if (nextYear <= endYear) {
                    setSelectedYear(nextYear);
                    setEndYear(nextYear);
                  }
                }}
              />
            </IconButton>
          </CustomStack>
          {/* //년도 선택 컴포넌트 */}
        </CustomStack>

        <CustomTableContainer>
          <CustomTable>
            <TableHead>
              <CustomTableRow>
                <CustomHeaderCell width="160px" colSpan={2} rowSpan={3} align="center">
                  {nameTitle}
                </CustomHeaderCell>
                <CustomHeaderCell colSpan={14} align="center">
                  금액(VAT별도)
                </CustomHeaderCell>
              </CustomTableRow>
              <CustomTableRow>
                <CustomHeaderCell width="180px" rowSpan={2} align="center">
                  합계
                </CustomHeaderCell>
                <CustomHeaderCell width="200px" align="center">
                  1월
                </CustomHeaderCell>
                <CustomHeaderCell width="200px" align="center">
                  2월
                </CustomHeaderCell>
                <CustomHeaderCell width="200px" align="center">
                  3월
                </CustomHeaderCell>
                <CustomHeaderCell width="200px" align="center">
                  4월
                </CustomHeaderCell>
                <CustomHeaderCell width="200px" align="center">
                  5월
                </CustomHeaderCell>
                <CustomHeaderCell width="200px" align="center">
                  6월
                </CustomHeaderCell>
                <CustomHeaderCell rowSpan={2} align="center">
                  관리
                </CustomHeaderCell>
              </CustomTableRow>
              <CustomTableRow>
                <CustomHeaderCell align="center">7월</CustomHeaderCell>
                <CustomHeaderCell align="center">8월</CustomHeaderCell>
                <CustomHeaderCell align="center">9월</CustomHeaderCell>
                <CustomHeaderCell align="center">10월</CustomHeaderCell>
                <CustomHeaderCell align="center">11월</CustomHeaderCell>
                <CustomHeaderCell align="center">12월</CustomHeaderCell>
              </CustomTableRow>
            </TableHead>
            <TableBody>
              {monthlyFinance.map((row, index) => {
                if (row[0] === selectedYear) {
                  const isFirstRow = index === 0;
                  const rowStyle = !isFirstRow ? { borderTop: '2px solid #d9d9d9' } : {};
                  const isAdded = row.isAdded === true;
                  return (
                    <React.Fragment key={index}>
                      <CustomTableRow style={rowStyle}>
                        {title !== '판관비' ? (
                          <CustomBodyCell rowSpan={2} colSpan={2}>
                            <CustomAutocomplete
                              disableClearable
                              value={row[14]}
                              options={searchCompanyList}
                              onChange={(event, value) => {
                                console.log('Selected value');
                                console.log(value);

                                // 매출 매입 외부인건비 입력 값이 null일 경우 공백 처리 (25.06.11)
                                if (value === null) value = '';

                                if (value || value === '') {
                                  setSearchCompany(value);
                                  handleMonthlyFinanceChange({ target: { value } }, index, 0);
                                }
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  onChange={(e) => {
                                    setSearchCompany(e.target.value);
                                    handleMonthlyFinanceChange({ target: { value: e.target.value } }, index, 0);
                                  }}
                                />
                              )}
                            />
                          </CustomBodyCell>
                        ) : (
                          <CustomBodyCell rowSpan={2} colSpan={2} sx={{ minWidth: '160px' }}>
                            <CustomTextField
                              placeholder="카드"
                              value={row[14]}
                              onChange={(e) =>
                                handleMonthlyFinanceChange({ target: { value: e.target.value } }, index, 0)
                              }
                            />
                          </CustomBodyCell>
                        )}
                        <CustomBodyCell rowSpan={2}>
                          <CustomTextField size="small" right value={formatAmount(calculateMonthlySum(row))} disabled />
                        </CustomBodyCell>
                        <CustomBodyCell>
                          <CustomTextField
                            size="small"
                            right
                            value={formatAmount(row[2])}
                            onChange={(event) => {
                              const value = event.target.value.replace(/[^0-9]/g, '');
                              event.target.value = value;
                              handleMonthlyFinanceChange(event, index, 1);
                            }}
                          />
                        </CustomBodyCell>
                        <CustomBodyCell>
                          <CustomTextField
                            size="small"
                            right
                            value={formatAmount(row[3])}
                            onChange={(event) => {
                              const value = event.target.value.replace(/[^0-9]/g, '');
                              event.target.value = value;
                              handleMonthlyFinanceChange(event, index, 2);
                            }}
                          />
                        </CustomBodyCell>
                        <CustomBodyCell>
                          <CustomTextField
                            size="small"
                            right
                            value={formatAmount(row[4])}
                            onChange={(event) => {
                              const value = event.target.value.replace(/[^0-9]/g, '');
                              event.target.value = value;
                              handleMonthlyFinanceChange(event, index, 3);
                            }}
                          />
                        </CustomBodyCell>
                        <CustomBodyCell>
                          <CustomTextField
                            size="small"
                            right
                            value={formatAmount(row[5])}
                            onChange={(event) => {
                              const value = event.target.value.replace(/[^0-9]/g, '');
                              event.target.value = value;
                              handleMonthlyFinanceChange(event, index, 4);
                            }}
                          />
                        </CustomBodyCell>
                        <CustomBodyCell>
                          <CustomTextField
                            size="small"
                            right
                            value={formatAmount(row[6])}
                            onChange={(event) => {
                              const value = event.target.value.replace(/[^0-9]/g, '');
                              event.target.value = value;
                              handleMonthlyFinanceChange(event, index, 5);
                            }}
                          />
                        </CustomBodyCell>
                        <CustomBodyCell>
                          <CustomTextField
                            size="small"
                            right
                            value={formatAmount(row[7])}
                            onChange={(event) => {
                              const value = event.target.value.replace(/[^0-9]/g, '');
                              event.target.value = value;
                              handleMonthlyFinanceChange(event, index, 6);
                            }}
                          />
                        </CustomBodyCell>
                        <CustomBodyCell rowSpan={2}>
                          <CustomStack justifyContent="center">
                            <CustomButton
                              variant="outlined"
                              size="small"
                              color="error"
                              onClick={() => handleDeleteMonthlyFinance(index)}
                            >
                              행삭제
                            </CustomButton>
                          </CustomStack>
                        </CustomBodyCell>
                      </CustomTableRow>
                      <CustomTableRow>
                        <CustomBodyCell>
                          <CustomTextField
                            size="small"
                            right
                            value={formatAmount(row[8])}
                            onChange={(event) => {
                              const value = event.target.value.replace(/[^0-9]/g, '');
                              event.target.value = value;
                              handleMonthlyFinanceChange(event, index, 7);
                            }}
                          />
                        </CustomBodyCell>
                        <CustomBodyCell>
                          <CustomTextField
                            right
                            size="small"
                            value={formatAmount(row[9])}
                            onChange={(event) => {
                              const value = event.target.value.replace(/[^0-9]/g, '');
                              event.target.value = value;
                              handleMonthlyFinanceChange(event, index, 8);
                            }}
                          />
                        </CustomBodyCell>
                        <CustomBodyCell>
                          <CustomTextField
                            size="small"
                            right
                            value={formatAmount(row[10])}
                            onChange={(event) => {
                              const value = event.target.value.replace(/[^0-9]/g, '');
                              event.target.value = value;
                              handleMonthlyFinanceChange(event, index, 9);
                            }}
                          />
                        </CustomBodyCell>
                        <CustomBodyCell>
                          <CustomTextField
                            size="small"
                            right
                            value={formatAmount(row[11])}
                            onChange={(event) => {
                              const value = event.target.value.replace(/[^0-9]/g, '');
                              event.target.value = value;
                              handleMonthlyFinanceChange(event, index, 10);
                            }}
                          />
                        </CustomBodyCell>
                        <CustomBodyCell>
                          <CustomTextField
                            size="small"
                            right
                            value={formatAmount(row[12])}
                            onChange={(event) => {
                              const value = event.target.value.replace(/[^0-9]/g, '');
                              event.target.value = value;
                              handleMonthlyFinanceChange(event, index, 11);
                            }}
                          />
                        </CustomBodyCell>
                        <CustomBodyCell>
                          <CustomTextField
                            size="small"
                            right
                            value={formatAmount(row[13])}
                            onChange={(event) => {
                              const value = event.target.value.replace(/[^0-9]/g, '');
                              event.target.value = value;
                              handleMonthlyFinanceChange(event, index, 12);
                            }}
                          />
                        </CustomBodyCell>
                      </CustomTableRow>
                    </React.Fragment>
                  );
                }
              })}
            </TableBody>
          </CustomTable>
        </CustomTableContainer>

        <CustomStack justifyContent="end" mt={2}>
          <CustomButton variant="outlined" size="small" startIcon={<AddIcon />} onClick={handleAddMonthlyFinance}>
            행추가
          </CustomButton>
        </CustomStack>
        </SectionWrapper>
      </React.Fragment>
    )
  );
});

export default ProjectCreateTableFinanceV2;
